import React, { useMemo, useCallback, useEffect, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight, Maximize2, X } from 'lucide-react';
import { usePlanningContext } from '../state/PlanningContext';
import { projectCategories } from '../../../utils/projectCategories';
import {
  countBucket,
  trackPlanningUpdatesEvent,
} from '../../../lib/planningUpdatesAnalytics';
import CategoryIcon from './CategoryIcons';

type CategoryApp = {
  id: string;
  name: string;
  icon: string;
};

interface CategoryDockProps {
  compact?: boolean;
}

const CategoryDock: React.FC<CategoryDockProps> = ({ compact = false }) => {
  const {
    selectedCategoryFilter,
    setSelectedCategoryFilter,
    multipleCategoryFilters,
    setMultipleCategoryFilters,
    categoryFilterMode,
    setCategoryFilterMode,
  } = usePlanningContext();
  const categoryRailRef = useRef<HTMLDivElement>(null);
  const expandedRailRef = useRef<HTMLDivElement>(null);
  const momentumFrameRef = useRef<number | null>(null);
  const lastMomentumTimeRef = useRef(0);
  const scrollVelocityRef = useRef(0);
  const dragStartXRef = useRef(0);
  const dragStartScrollLeftRef = useRef(0);
  const lastDragXRef = useRef(0);
  const lastDragTimeRef = useRef(0);
  const pointerMovedRef = useRef(false);
  const pointerDownCategoryIdRef = useRef<string | null>(null);
  const suppressCategoryClickUntilRef = useRef(0);
  const isDraggingCategoriesRef = useRef(false);
  const categoryBrowserInitialCategoryIdRef = useRef<string | null>(null);
  const expandedSyncActiveFromScrollRef = useRef(false);
  const [isDraggingCategories, setIsDraggingCategories] = useState(false);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [hoverCategoryId, setHoverCategoryId] = useState<string | null>(null);
  const [railFocusCategoryId, setRailFocusCategoryId] = useState<string | null>(null);
  const [categoryBrowserOpen, setCategoryBrowserOpen] = useState(false);
  const [expandedCategoryId, setExpandedCategoryId] = useState<string | null>(null);
  const [expandedCanScrollLeft, setExpandedCanScrollLeft] = useState(false);
  const [expandedCanScrollRight, setExpandedCanScrollRight] = useState(false);

  const apps: CategoryApp[] = useMemo(() => {
    return projectCategories
      .filter(cat => cat.id !== 'uncategorized')
      .map(cat => ({
        id: cat.id,
        name: cat.label,
        icon: cat.icon,
      }));
  }, []);

  const openApps = useMemo(() => {
    if (multipleCategoryFilters && multipleCategoryFilters.length > 0) {
      return multipleCategoryFilters;
    }
    return selectedCategoryFilter ? [selectedCategoryFilter] : [];
  }, [multipleCategoryFilters, selectedCategoryFilter]);

  const applyCategoryFilter = useCallback((categoryId: string) => {
    const normalized = categoryId.replace('__low', '');
    const exists = (multipleCategoryFilters || []).includes(normalized);
    let next = [] as string[];
    if (exists) {
      next = (multipleCategoryFilters || []).filter(id => id !== normalized);
    } else {
      next = [...(multipleCategoryFilters || []), normalized];
    }

    trackPlanningUpdatesEvent('planning_updates_category_selected', {
      placement: compact ? 'category_dock_compact' : 'category_dock',
      status: normalized,
      reason: exists ? 'removed' : 'selected',
      value: next.length,
      metadata: {
        category_id: normalized,
        category_state: exists ? 'removed' : 'selected',
        category_mode: categoryFilterMode,
        selected_category_count: next.length,
        selected_category_bucket: countBucket(next.length)
      }
    });

    setMultipleCategoryFilters(next);
    setSelectedCategoryFilter(next[0] || null);
  }, [categoryFilterMode, compact, multipleCategoryFilters, setMultipleCategoryFilters, setSelectedCategoryFilter]);

  const handleAppClick = useCallback((app: CategoryApp) => {
    applyCategoryFilter(app.id);
  }, [applyCategoryFilter]);

  const handleClearCategories = useCallback(() => {
    trackPlanningUpdatesEvent('planning_updates_categories_cleared', {
      placement: compact ? 'category_dock_compact' : 'category_dock',
      status: countBucket(openApps.length),
      metadata: {
        cleared_category_count: openApps.length,
        category_mode: categoryFilterMode
      }
    });
    setMultipleCategoryFilters([]);
    setSelectedCategoryFilter(null);
  }, [categoryFilterMode, compact, openApps.length, setMultipleCategoryFilters, setSelectedCategoryFilter]);

  const handleCategoryModeChange = useCallback((mode: 'include' | 'exclude') => {
    trackPlanningUpdatesEvent('planning_updates_category_mode_changed', {
      placement: compact ? 'category_dock_compact' : 'category_dock',
      status: mode,
      reason: categoryFilterMode,
      metadata: {
        previous_category_mode: categoryFilterMode,
        next_category_mode: mode,
        selected_category_count: openApps.length
      }
    });
    setCategoryFilterMode(mode);
  }, [categoryFilterMode, compact, openApps.length, setCategoryFilterMode]);

  const hasSelection = (multipleCategoryFilters?.length ?? 0) > 0 || Boolean(selectedCategoryFilter);
  const openSet = useMemo(() => new Set(openApps.map((id) => id.replace('__low', ''))), [openApps]);
  const activeRailCategoryId = hoverCategoryId ?? railFocusCategoryId ?? openApps[0]?.replace('__low', '') ?? null;
  const expandedCategory = useMemo(() => {
    return apps.find((app) => app.id === expandedCategoryId) ?? apps[0] ?? null;
  }, [apps, expandedCategoryId]);
  const expandedCategoryIsSelected = expandedCategory ? openSet.has(expandedCategory.id) : false;

  const handleOpenCategoryBrowser = useCallback(() => {
    const initialCategoryId = openApps[0]?.replace('__low', '') ?? activeRailCategoryId ?? apps[0]?.id ?? null;
    categoryBrowserInitialCategoryIdRef.current = initialCategoryId;
    expandedSyncActiveFromScrollRef.current = false;
    setExpandedCategoryId(initialCategoryId);
    setCategoryBrowserOpen(true);

    trackPlanningUpdatesEvent('planning_updates_category_browser_opened', {
      placement: compact ? 'category_dock_compact' : 'category_dock',
      status: countBucket(apps.length),
      metadata: {
        category_count: apps.length,
        selected_category_count: openApps.length,
        category_mode: categoryFilterMode,
      },
    });
  }, [activeRailCategoryId, apps, categoryFilterMode, compact, openApps]);

  const handleCloseCategoryBrowser = useCallback(() => {
    categoryBrowserInitialCategoryIdRef.current = null;
    setCategoryBrowserOpen(false);
  }, []);

  const handleCategoryBrowserBackdropClick = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
    if (event.target === event.currentTarget) {
      categoryBrowserInitialCategoryIdRef.current = null;
      setCategoryBrowserOpen(false);
    }
  }, []);

  const handleExpandedCategoryClick = useCallback((app: CategoryApp) => {
    setExpandedCategoryId(app.id);
    applyCategoryFilter(app.id);
  }, [applyCategoryFilter]);

  const handleExpandedCategoryAction = useCallback(() => {
    if (!expandedCategory) return;
    applyCategoryFilter(expandedCategory.id);
  }, [applyCategoryFilter, expandedCategory]);

  useEffect(() => {
    if (!categoryBrowserOpen) return undefined;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        categoryBrowserInitialCategoryIdRef.current = null;
        setCategoryBrowserOpen(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [categoryBrowserOpen]);

  const updateScrollState = useCallback(() => {
    const rail = categoryRailRef.current;
    if (!rail) return;

    const maxScrollLeft = Math.max(0, rail.scrollWidth - rail.clientWidth);
    setCanScrollLeft(rail.scrollLeft > 2);
    setCanScrollRight(rail.scrollLeft < maxScrollLeft - 2);

    const railRect = rail.getBoundingClientRect();
    const railCenter = railRect.left + railRect.width / 2;
    let closestCategoryId: string | null = null;
    let closestDistance = Number.POSITIVE_INFINITY;

    rail.querySelectorAll<HTMLElement>('[data-category-id]').forEach((tile) => {
      const tileRect = tile.getBoundingClientRect();
      const tileCenter = tileRect.left + tileRect.width / 2;
      const distance = Math.abs(tileCenter - railCenter);
      if (distance < closestDistance) {
        closestDistance = distance;
        closestCategoryId = tile.dataset.categoryId ?? null;
      }
    });

    setRailFocusCategoryId((current) => (current === closestCategoryId ? current : closestCategoryId));
  }, []);

  const updateExpandedScrollState = useCallback(() => {
    const rail = expandedRailRef.current;
    if (!rail) return;

    const maxScrollLeft = Math.max(0, rail.scrollWidth - rail.clientWidth);
    setExpandedCanScrollLeft(rail.scrollLeft > 2);
    setExpandedCanScrollRight(rail.scrollLeft < maxScrollLeft - 2);

    const railRect = rail.getBoundingClientRect();
    const railCenter = railRect.left + railRect.width / 2;
    let closestCategoryId: string | null = null;
    let closestDistance = Number.POSITIVE_INFINITY;

    rail.querySelectorAll<HTMLElement>('[data-category-id]').forEach((tile) => {
      const tileRect = tile.getBoundingClientRect();
      const tileCenter = tileRect.left + tileRect.width / 2;
      const distance = Math.abs(tileCenter - railCenter);
      if (distance < closestDistance) {
        closestDistance = distance;
        closestCategoryId = tile.dataset.categoryId ?? null;
      }
    });

    if (closestCategoryId && expandedSyncActiveFromScrollRef.current) {
      setExpandedCategoryId((current) => (current === closestCategoryId ? current : closestCategoryId));
    }
  }, []);

  const stopMomentumScroll = useCallback(() => {
    if (momentumFrameRef.current !== null) {
      cancelAnimationFrame(momentumFrameRef.current);
      momentumFrameRef.current = null;
    }
    scrollVelocityRef.current = 0;
  }, []);

  const startMomentumScroll = useCallback(() => {
    const rail = categoryRailRef.current;
    if (!rail || Math.abs(scrollVelocityRef.current) < 0.03) {
      stopMomentumScroll();
      return;
    }

    const initialVelocity = scrollVelocityRef.current;
    stopMomentumScroll();
    scrollVelocityRef.current = initialVelocity;
    lastMomentumTimeRef.current = performance.now();

    const tick = (now: number) => {
      const currentRail = categoryRailRef.current;
      if (!currentRail) {
        stopMomentumScroll();
        return;
      }

      const deltaTime = Math.max(8, Math.min(34, now - lastMomentumTimeRef.current));
      lastMomentumTimeRef.current = now;

      const velocity = scrollVelocityRef.current;
      const before = currentRail.scrollLeft;
      currentRail.scrollLeft += velocity * deltaTime;
      const moved = Math.abs(currentRail.scrollLeft - before) > 0.08;
      const decay = Math.pow(0.92, deltaTime / 16.67);
      scrollVelocityRef.current = velocity * decay;
      updateScrollState();

      if (!moved || Math.abs(scrollVelocityRef.current) < 0.01) {
        stopMomentumScroll();
        return;
      }

      momentumFrameRef.current = requestAnimationFrame(tick);
    };

    momentumFrameRef.current = requestAnimationFrame(tick);
  }, [stopMomentumScroll, updateScrollState]);

  const handleCategoryRailPointerDown = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    const rail = categoryRailRef.current;
    if (!rail || rail.scrollWidth <= rail.clientWidth + 1) return;

    stopMomentumScroll();
    isDraggingCategoriesRef.current = true;
    setIsDraggingCategories(true);
    dragStartXRef.current = event.clientX;
    dragStartScrollLeftRef.current = rail.scrollLeft;
    lastDragXRef.current = event.clientX;
    lastDragTimeRef.current = performance.now();
    pointerMovedRef.current = false;
    const categoryTile = (event.target as HTMLElement).closest<HTMLElement>('[data-category-id]');
    pointerDownCategoryIdRef.current = categoryTile?.dataset.categoryId ?? null;
    scrollVelocityRef.current = 0;
    event.currentTarget.setPointerCapture(event.pointerId);
  }, [stopMomentumScroll]);

  const handleCategoryRailPointerMove = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    if (!isDraggingCategoriesRef.current) return;

    const rail = categoryRailRef.current;
    if (!rail) return;

    const dragDelta = event.clientX - dragStartXRef.current;
    rail.scrollLeft = dragStartScrollLeftRef.current - dragDelta;

    const now = performance.now();
    const deltaX = event.clientX - lastDragXRef.current;
    const deltaTime = now - lastDragTimeRef.current;
    if (deltaTime > 0) {
      const instantVelocity = -deltaX / deltaTime;
      scrollVelocityRef.current = (scrollVelocityRef.current * 0.35) + (instantVelocity * 0.65);
    }

    lastDragXRef.current = event.clientX;
    lastDragTimeRef.current = now;
    if (!pointerMovedRef.current && Math.abs(dragDelta) > 4) {
      pointerMovedRef.current = true;
    }
    updateScrollState();
  }, [updateScrollState]);

  const handleCategoryRailPointerUp = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    if (!isDraggingCategoriesRef.current) return;

    isDraggingCategoriesRef.current = false;
    setIsDraggingCategories(false);
    try {
      event.currentTarget.releasePointerCapture(event.pointerId);
    } catch (_error) {
      void _error;
    }

    if (pointerMovedRef.current) {
      suppressCategoryClickUntilRef.current = performance.now() + 250;
      startMomentumScroll();
    } else {
      const categoryId = pointerDownCategoryIdRef.current;

      if (categoryId) {
        const app = apps.find((candidate) => candidate.id === categoryId);
        if (app) {
          suppressCategoryClickUntilRef.current = performance.now() + 250;
          handleAppClick(app);
        }
      }
      stopMomentumScroll();
    }
    pointerDownCategoryIdRef.current = null;
  }, [apps, handleAppClick, startMomentumScroll, stopMomentumScroll]);

  const handleCategoryRailPointerCancel = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    if (!isDraggingCategoriesRef.current) return;

    isDraggingCategoriesRef.current = false;
    setIsDraggingCategories(false);
    try {
      event.currentTarget.releasePointerCapture(event.pointerId);
    } catch (_error) {
      void _error;
    }
    pointerMovedRef.current = false;
    pointerDownCategoryIdRef.current = null;
    stopMomentumScroll();
  }, [stopMomentumScroll]);

  const handleCategoryTileClick = useCallback((app: CategoryApp) => {
    if (performance.now() < suppressCategoryClickUntilRef.current) return;
    handleAppClick(app);
  }, [handleAppClick]);

  const scrollCategoryRail = useCallback((direction: -1 | 1) => {
    const rail = categoryRailRef.current;
    if (!rail) return;

    stopMomentumScroll();
    const distance = Math.max(220, rail.clientWidth * 0.72);
    rail.scrollBy({ left: direction * distance, behavior: 'smooth' });
    window.setTimeout(updateScrollState, 180);
  }, [stopMomentumScroll, updateScrollState]);

  const scrollExpandedCategoryRail = useCallback((direction: -1 | 1) => {
    const rail = expandedRailRef.current;
    if (!rail) return;

    expandedSyncActiveFromScrollRef.current = true;
    const distance = Math.max(220, rail.clientWidth * 0.72);
    rail.scrollBy({ left: direction * distance, behavior: 'smooth' });
    window.setTimeout(updateExpandedScrollState, 220);
  }, [updateExpandedScrollState]);

  useEffect(() => {
    const rail = categoryRailRef.current;
    if (!rail) return undefined;

    const handleScroll = () => updateScrollState();
    const handleWheel = (event: WheelEvent) => {
      if (Math.abs(event.deltaX) >= Math.abs(event.deltaY)) return;
      if (rail.scrollWidth <= rail.clientWidth + 1) return;

      rail.scrollLeft += event.deltaY * 1.4;
      updateScrollState();
      event.preventDefault();
    };
    const handleResize = () => updateScrollState();

    updateScrollState();
    rail.addEventListener('scroll', handleScroll, { passive: true });
    rail.addEventListener('wheel', handleWheel, { passive: false });
    window.addEventListener('resize', handleResize);

    return () => {
      rail.removeEventListener('scroll', handleScroll);
      rail.removeEventListener('wheel', handleWheel);
      window.removeEventListener('resize', handleResize);
    };
  }, [apps.length, updateScrollState]);

  useEffect(() => {
    if (!categoryBrowserOpen) return undefined;
    const rail = expandedRailRef.current;
    if (!rail) return undefined;

    const handleScroll = () => updateExpandedScrollState();
    const handleWheel = (event: WheelEvent) => {
      if (Math.abs(event.deltaX) >= Math.abs(event.deltaY)) return;
      if (rail.scrollWidth <= rail.clientWidth + 1) return;

      expandedSyncActiveFromScrollRef.current = true;
      rail.scrollLeft += event.deltaY * 1.4;
      updateExpandedScrollState();
      event.preventDefault();
    };
    const handleResize = () => updateExpandedScrollState();

    updateExpandedScrollState();
    rail.addEventListener('scroll', handleScroll, { passive: true });
    rail.addEventListener('wheel', handleWheel, { passive: false });
    window.addEventListener('resize', handleResize);

    return () => {
      rail.removeEventListener('scroll', handleScroll);
      rail.removeEventListener('wheel', handleWheel);
      window.removeEventListener('resize', handleResize);
    };
  }, [apps.length, categoryBrowserOpen, updateExpandedScrollState]);

  useEffect(() => {
    if (!categoryBrowserOpen || !categoryBrowserInitialCategoryIdRef.current) return undefined;

    const timeoutId = window.setTimeout(() => {
      const rail = expandedRailRef.current;
      if (!rail) return;

      const initialCategoryId = categoryBrowserInitialCategoryIdRef.current;
      const tile = Array.from(rail.querySelectorAll<HTMLElement>('[data-category-id]'))
        .find((element) => element.dataset.categoryId === initialCategoryId);
      if (!tile) return;

      tile.scrollIntoView({ behavior: 'auto', block: 'nearest', inline: 'center' });
      window.setTimeout(updateExpandedScrollState, 80);
    }, 80);

    return () => window.clearTimeout(timeoutId);
  }, [categoryBrowserOpen, updateExpandedScrollState]);

  useEffect(() => () => {
    stopMomentumScroll();
  }, [stopMomentumScroll]);

  useEffect(() => {
    const rail = categoryRailRef.current;
    if (!rail || openApps.length === 0) return undefined;

    const selectedId = openApps[0]?.replace('__low', '');
    if (!selectedId) return undefined;

    const timeoutId = window.setTimeout(() => {
      const tile = Array.from(rail.querySelectorAll<HTMLElement>('[data-category-id]'))
        .find((element) => element.dataset.categoryId === selectedId);
      if (!tile) return;
      tile.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
      window.setTimeout(updateScrollState, 180);
    }, 80);

    return () => window.clearTimeout(timeoutId);
  }, [openApps, updateScrollState]);

  return (
    <div className="relative w-full" data-category-dock-root>
      <div className={`pr-panel w-full ${compact ? 'px-4 py-4' : 'px-5 py-5 sm:px-6'}`}>
        <div className="flex flex-wrap items-baseline justify-between gap-3">
          <p className="pr-section-label min-w-[180px] flex-1">
            Categories
            {(multipleCategoryFilters?.length ?? 0) > 0 && (
              <span className="pr-data normal-case tracking-normal">{multipleCategoryFilters.length} selected</span>
            )}
          </p>

          <div className="flex shrink-0 items-center gap-3">
            <button
              type="button"
              onClick={handleOpenCategoryBrowser}
              className="pr-textlink inline-flex items-center gap-1 text-xs"
              title="Open expanded category browser"
            >
              <Maximize2 className="h-3.5 w-3.5" />
              Browse all
            </button>
            <div className="pr-segment">
              <button
                onClick={() => handleCategoryModeChange('include')}
                aria-pressed={categoryFilterMode === 'include'}
                className="pr-segment-item !py-1.5 text-xs"
              >
                Include
              </button>
              <button
                onClick={() => handleCategoryModeChange('exclude')}
                aria-pressed={categoryFilterMode === 'exclude'}
                className="pr-segment-item !py-1.5 text-xs"
              >
                Exclude
              </button>
            </div>
            <button
              onClick={handleClearCategories}
              disabled={!hasSelection}
              className={`pr-textlink text-xs ${!hasSelection ? 'cursor-not-allowed opacity-40' : ''}`}
              title="Clear all selected categories"
            >
              Clear
            </button>
          </div>
        </div>

        <div
          className="pr-category-scroll-shell mt-5"
          data-can-scroll-left={canScrollLeft ? 'true' : 'false'}
          data-can-scroll-right={canScrollRight ? 'true' : 'false'}
        >
          <button
            type="button"
            onClick={() => scrollCategoryRail(-1)}
            disabled={!canScrollLeft}
            className="pr-category-scroll-control pr-category-scroll-control--left"
            aria-label="Scroll categories left"
            title="Scroll categories left"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>

          <div
            ref={categoryRailRef}
            role="group"
            aria-label="Planning categories"
            className={`pr-category-grid ${isDraggingCategories ? 'is-dragging' : ''}`}
            onMouseLeave={() => setHoverCategoryId(null)}
            onPointerDown={handleCategoryRailPointerDown}
            onPointerMove={handleCategoryRailPointerMove}
            onPointerUp={handleCategoryRailPointerUp}
            onPointerCancel={handleCategoryRailPointerCancel}
            onDragStart={(event) => event.preventDefault()}
          >
            {apps.map((app) => {
              const isOn = openSet.has(app.id);
              return (
                <button
                  key={`categorydock-${app.id}`}
                  data-category-id={app.id}
                  data-rail-active={activeRailCategoryId === app.id ? 'true' : 'false'}
                  type="button"
                  aria-label={app.name}
                  aria-pressed={isOn}
                  onMouseEnter={() => setHoverCategoryId(app.id)}
                  onFocus={() => setHoverCategoryId(app.id)}
                  onBlur={() => setHoverCategoryId(null)}
                  onClick={() => handleCategoryTileClick(app)}
                  className="pr-category-tile"
                  title={app.name}
                >
                  <span className="pr-category-tile-icon">
                    <CategoryIcon categoryId={app.id} size={70} />
                  </span>
                  <span className="pr-category-tile-label">{app.name}</span>
                </button>
              );
            })}
          </div>

          <button
            type="button"
            onClick={() => scrollCategoryRail(1)}
            disabled={!canScrollRight}
            className="pr-category-scroll-control pr-category-scroll-control--right"
            aria-label="Scroll categories right"
            title="Scroll categories right"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>

        {categoryFilterMode === 'exclude' && hasSelection && (
          <p className="pr-data mt-4 text-[var(--c-error)]">
            Excluding the selected categories from results.
          </p>
        )}
      </div>

      {categoryBrowserOpen && expandedCategory && (
        <div
          className="planning-register pr-category-browser-backdrop"
          onMouseDown={handleCategoryBrowserBackdropClick}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="category-browser-title"
            className="pr-category-browser-dialog"
          >
            <div className="pr-category-browser-header">
              <p className="pr-section-label flex-1">Category browser</p>
              <button
                type="button"
                onClick={handleCloseCategoryBrowser}
                className="pr-category-browser-close"
                aria-label="Close category browser"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="pr-category-browser-body">
              <div className="pr-category-browser-feature">
                <div className="pr-category-browser-art">
                  <CategoryIcon categoryId={expandedCategory.id} size={260} className="h-full w-full" />
                </div>
                <h3 id="category-browser-title" className="pr-category-browser-title">
                  {expandedCategory.name}
                </h3>
                <p className="pr-data mt-2">
                  {expandedCategoryIsSelected ? 'Selected category' : 'Planning category'}
                </p>
                <button type="button" onClick={handleExpandedCategoryAction} className="pr-button mt-5">
                  {expandedCategoryIsSelected ? 'Remove filter' : 'Apply filter'}
                </button>
              </div>

              <div
                className="pr-category-scroll-shell pr-category-scroll-shell--modal"
                data-can-scroll-left={expandedCanScrollLeft ? 'true' : 'false'}
                data-can-scroll-right={expandedCanScrollRight ? 'true' : 'false'}
              >
                <button
                  type="button"
                  onClick={() => scrollExpandedCategoryRail(-1)}
                  disabled={!expandedCanScrollLeft}
                  className="pr-category-scroll-control pr-category-scroll-control--left"
                  aria-label="Scroll expanded categories left"
                  title="Scroll categories left"
                >
                  <ChevronLeft className="h-4 w-4" />
                </button>

                <div
                  ref={expandedRailRef}
                  role="group"
                  aria-label="Expanded planning categories"
                  className="pr-category-grid pr-category-grid--modal"
                  onMouseLeave={() => setExpandedCategoryId(expandedCategory.id)}
                  onDragStart={(event) => event.preventDefault()}
                >
                  {apps.map((app) => {
                    const isOn = openSet.has(app.id);
                    const isActive = expandedCategory.id === app.id;
                    return (
                      <button
                        key={`expanded-category-${app.id}`}
                        data-category-id={app.id}
                        data-rail-active={isActive ? 'true' : 'false'}
                        type="button"
                        aria-label={app.name}
                        aria-pressed={isOn}
                        onMouseEnter={() => setExpandedCategoryId(app.id)}
                        onFocus={() => setExpandedCategoryId(app.id)}
                        onClick={() => handleExpandedCategoryClick(app)}
                        className="pr-category-tile"
                        title={app.name}
                      >
                        <span className="pr-category-tile-icon">
                          <CategoryIcon categoryId={app.id} size={70} />
                        </span>
                        <span className="pr-category-tile-label">{app.name}</span>
                      </button>
                    );
                  })}
                </div>

                <button
                  type="button"
                  onClick={() => scrollExpandedCategoryRail(1)}
                  disabled={!expandedCanScrollRight}
                  className="pr-category-scroll-control pr-category-scroll-control--right"
                  aria-label="Scroll expanded categories right"
                  title="Scroll categories right"
                >
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CategoryDock;
